'use client';

import { format, formatDistanceToNowStrict } from 'date-fns';
import { useLocation } from '@/data/sources/location';
import type { FC } from 'react';
import { ContextualPopup, Divider, Flex, Typography } from '@/ui';
import { GlobeIndicator } from './GlobeIndicator';
import { HOME } from './location';

const deviceName = (source: string) =>
  source.startsWith('iOS')
    ? 'iPhone'
    : source.startsWith('watchOS')
      ? 'Apple Watch'
      : 'Unknown device';

export const GlobeContextualPopup: FC<{
  onConnected?: () => void;
  onOpenChange?: (open: boolean) => void;
}> = ({ onConnected, onOpenChange }) => {
  const { data: location } = useLocation();
  const city = location?.city ?? HOME.city;
  const country = location?.country ?? HOME.country;
  const latitude = location?.latitude ?? HOME.lat;
  const longitude = location?.longitude ?? HOME.lon;
  const awayFromHome = city !== HOME.city;

  return (
    <ContextualPopup
      behaviour="click"
      onOpenChange={onOpenChange}
      handle={<GlobeIndicator onConnected={onConnected} />}
    >
      <ContextualPopup.WorldMap lat={latitude} long={longitude} />

      <ContextualPopup.Section paddingTop={0}>
        <Typography.Caption textAlign="center" opacity={0.5}>
          {country}, {city}
        </Typography.Caption>

        {awayFromHome && (
          <Typography.Caption textAlign="center" opacity={0.35}>
            Currently away · based in {HOME.city}
          </Typography.Caption>
        )}
      </ContextualPopup.Section>

      {location?.lastSync && (
        <>
          <Divider.Horizontal />

          <ContextualPopup.Section>
            <Flex justifyContent="space-between" alignItems="center">
              <Flex flexDirection="column" alignItems="start">
                <Typography.Caption size="L">
                  Last Synchronization
                </Typography.Caption>

                {location.source &&
                  location.source.toLowerCase() !== 'unknown' && (
                    <Typography.Caption opacity={0.5} textAlign="left">
                      from {deviceName(location.source)}
                    </Typography.Caption>
                  )}
              </Flex>

              <Flex flexDirection="column" alignItems="end">
                <Typography.Caption size="L">
                  {formatDistanceToNowStrict(location.lastSync, {
                    addSuffix: true,
                  })}
                </Typography.Caption>

                <Typography.Caption opacity={0.5} textAlign="left">
                  {format(location.lastSync, 'dd. MMM. yyyy, p')}
                </Typography.Caption>
              </Flex>
            </Flex>
          </ContextualPopup.Section>
        </>
      )}

      <Divider.Horizontal />

      <ContextualPopup.Section>
        <Typography.Caption opacity={0.5}>
          The night sky you're seeing is the same sky I see when I look up. It's
          adjusted to my exact location and synchronized with the current phase
          of Earth's rotation.
          <br />
          <br />
          The space imagery is sourced from NASA.
        </Typography.Caption>
      </ContextualPopup.Section>
    </ContextualPopup>
  );
};
